import { createAppRequest } from '@/services/request';

const request = createAppRequest();
const API_PREFIX = '/bff/platform-settings';

export type StorageAccessMode =
  | 'private'
  | 'public_read'
  | 'public_folder_only';

export interface PlatformBrandingSettings {
  platformName: string;
  platformNameEn: string;
  logoLight: string;
  logoDark: string;
  favicon: string;
  footerCopyright: string;
}

export interface PlatformLlmSettings {
  defaultChatModelRef: string;
  defaultEmbeddingModelRef: string;
}

export interface PlatformStorageSettings {
  autoProvisionOnAppCreate: boolean;
  /** 空字符串：沿用文件管理服务的默认访问模式 */
  defaultAccessMode: StorageAccessMode | '';
  defaultUsageEnabled: boolean;
}

/** 0 表示不限制 / 不发放 */
export interface PlatformQuotaSettings {
  defaultTenantTokenQuota: number;
  defaultTenantStorageQuotaGb: number;
  maxMembersPerTenant: number;
  maxInstalledAppsPerTenant: number;
}

export interface PlatformFeatureSettings {
  aiAssistantEnabled: boolean;
}

export interface PlatformSettings {
  branding: PlatformBrandingSettings;
  llm: PlatformLlmSettings;
  storage: PlatformStorageSettings;
  quota: PlatformQuotaSettings;
  features: PlatformFeatureSettings;
}

export interface PlatformPublicProfile {
  branding: PlatformBrandingSettings;
  features: PlatformFeatureSettings;
}

export interface PlatformResourceDefinition {
  code: string;
  name: string;
  /** 所属菜单模块，用于平台权限页分组展示 */
  group?: string;
  actions: string[];
}

export interface PlatformRole {
  code: string;
  name: string;
  builtin: boolean;
  /** 平台管理员：拥有全部权限且不可修改 */
  locked: boolean;
}

export interface PlatformRoleMember {
  userId: string;
  displayName?: string | null;
  assignedAt?: string | null;
}

export function getPlatformSettings() {
  return request<PlatformSettings>(API_PREFIX, { method: 'GET' });
}

export function updatePlatformSettingsSection<K extends keyof PlatformSettings>(
  section: K,
  payload: Partial<PlatformSettings[K]>,
) {
  return request<PlatformSettings[K]>(`${API_PREFIX}/${section}`, {
    method: 'PUT',
    data: payload,
  });
}

export function getPlatformPublicProfile() {
  return request<PlatformPublicProfile>('/bff/public/platform-profile', {
    method: 'GET',
    skipErrorHandler: true,
  });
}

const PERMISSIONS_PREFIX = `${API_PREFIX}/permissions`;
const roleUrl = (code: string) =>
  `${PERMISSIONS_PREFIX}/roles/${encodeURIComponent(code)}`;

export function listPlatformResources() {
  return request<PlatformResourceDefinition[]>(
    `${PERMISSIONS_PREFIX}/resources`,
    { method: 'GET' },
  );
}

export function listPlatformRoles() {
  return request<PlatformRole[]>(`${PERMISSIONS_PREFIX}/roles`, {
    method: 'GET',
  });
}

export function createPlatformRole(name: string) {
  return request<PlatformRole>(`${PERMISSIONS_PREFIX}/roles`, {
    method: 'POST',
    data: { name },
  });
}

export function renamePlatformRole(code: string, name: string) {
  return request<{ success: boolean }>(roleUrl(code), {
    method: 'PATCH',
    data: { name },
  });
}

export function deletePlatformRole(code: string) {
  return request<{ success: boolean }>(roleUrl(code), { method: 'DELETE' });
}

export function getPlatformRolePermissions(code: string) {
  return request<string[]>(`${roleUrl(code)}/permissions`, { method: 'GET' });
}

export function setPlatformRolePermissions(
  code: string,
  permissions: string[],
) {
  return request<string[]>(`${roleUrl(code)}/permissions`, {
    method: 'PUT',
    data: { permissions },
  });
}

export function listPlatformRoleMembers(code: string) {
  return request<PlatformRoleMember[]>(`${roleUrl(code)}/members`, {
    method: 'GET',
  });
}

export function addPlatformRoleMember(code: string, userId: string) {
  return request<{ success: boolean }>(`${roleUrl(code)}/members`, {
    method: 'POST',
    data: { userId },
  });
}

export function removePlatformRoleMember(code: string, userId: string) {
  return request<{ success: boolean }>(
    `${roleUrl(code)}/members/${encodeURIComponent(userId)}`,
    { method: 'DELETE' },
  );
}
